import _ from "lodash";
import { languageSchema } from "./languageSchema";
import { FEATURE_FLAGS } from "./featureFlag";
import { makeI18nField } from "../utils/i18nSchemas";

export const DEFAULT_EVENT_ACCENT_COLOR = "#102694";
const SLUG_FORMAT_STR = "^[a-z0-9]+(?:-[a-z0-9]+)*$";
// All-digit slugs cannot be set, so a numeric public URL can never resolve to an
// event other than the one whose backfilled slug it was.
export const SLUG_PATTERN_STR = "^(?!\\d+$)[a-z0-9]+(?:-[a-z0-9]+)*$";
export const SLUG_MAX_LENGTH = 100;
const slugSchema = {
  type: "string",
  minLength: 1,
  maxLength: SLUG_MAX_LENGTH,
  pattern: SLUG_PATTERN_STR,
};
// Update payloads echo back the slug an event already has, which for events
// still on the backfill is all digits. The service rejects those on change.
const storedSlugSchema = { ...slugSchema, pattern: SLUG_FORMAT_STR };

export const HOME_SCREEN_SECTION_KEYS = [
  "nowNext",
  "quickActions",
  "giveaway",
  "polls",
  "surveys",
  "announcements",
  "specialInvitations",
  "agenda",
  "speakers",
  "sponsors",
];

// Home screen section metadata. `feature` is the event flag that has to be on
// for the section to appear at all — an organizer can't make it visible from
// the home screen settings while it's off. Sections without one are always
// available. `chipOf` names the section a key is drawn inside of as an action
// chip: it can still be switched off on its own, but it has no place of its
// own in `order`, so the settings page nests it under its parent's card.
export const HOME_SCREEN_SECTIONS_METADATA = {
  nowNext: {
    label: "Now & next",
    description: "The session happening now, or the one coming up.",
  },
  quickActions: {
    label: "Quick actions",
    description: "Shortcut chips for whatever is waiting on the attendee.",
  },
  giveaway: {
    label: "Giveaway",
    description: "A chip while a giveaway is running.",
    feature: FEATURE_FLAGS.GIVEAWAYS,
    chipOf: "quickActions",
  },
  polls: {
    label: "Polls",
    description: "A chip while a session has polls open.",
    feature: FEATURE_FLAGS.POLLS,
    chipOf: "quickActions",
  },
  surveys: {
    label: "Surveys",
    description: "A chip while a survey is waiting on an answer.",
    feature: FEATURE_FLAGS.SURVEYS,
    chipOf: "quickActions",
  },
  announcements: {
    label: "Announcements",
    description: "Banner cards for the event's announcements.",
  },
  specialInvitations: {
    label: "Special invitations",
    description: "Invitation buttons below the banner cards.",
    chipOf: "announcements",
  },
  agenda: {
    label: "Agenda",
    description: "Timeline of the current day's sessions.",
  },
  speakers: {
    label: "Speakers",
    description: "A random pick of speakers, or a list you curate.",
  },
  sponsors: {
    label: "Sponsors",
    description: "The sponsor levels the organizer picked.",
  },
};

export const HOME_SCREEN_CARD_STYLES = ["compact", "grid", "wide"];

export const HOME_SCREEN_SPEAKER_MODES = ["random", "curated"];
export const HOME_SCREEN_MAX_SPEAKERS = 10;
export const HOME_SCREEN_MAX_AGENDA_SESSIONS = 10;

// What a section shows when the organizer hasn't configured it. Read by both
// the app and the settings page so the two can't disagree. `levels: null` is
// "unconfigured", which the sponsors section reads as the top level alone.
export const HOME_SCREEN_SECTION_DEFAULTS = {
  sponsors: { levels: null, cardStyle: "compact" },
  agenda: { count: 4 },
  speakers: {
    mode: "random",
    count: HOME_SCREEN_MAX_SPEAKERS,
    speakerIds: [],
  },
};

const sectionKeyListSchema = {
  type: "array",
  items: { type: "string", enum: HOME_SCREEN_SECTION_KEYS },
  uniqueItems: true,
};

const homeScreenSchema = {
  type: ["object", "null"],
  properties: {
    hidden: sectionKeyListSchema,
    order: sectionKeyListSchema,
    options: {
      type: "object",
      properties: {
        sponsors: {
          type: "object",
          properties: {
            levels: {
              type: ["array", "null"],
              items: { type: "integer", minimum: 1 },
              uniqueItems: true,
            },
            cardStyle: { type: "string", enum: HOME_SCREEN_CARD_STYLES },
          },
          additionalProperties: false,
        },
        agenda: {
          type: "object",
          properties: {
            // How many of the day's sessions the timeline shows at once.
            count: {
              type: "integer",
              minimum: 1,
              maximum: HOME_SCREEN_MAX_AGENDA_SESSIONS,
            },
          },
          additionalProperties: false,
        },
        speakers: {
          type: "object",
          properties: {
            mode: { type: "string", enum: HOME_SCREEN_SPEAKER_MODES },
            // Random mode only: how many of the pool the row shows.
            count: {
              type: "integer",
              minimum: 1,
              maximum: HOME_SCREEN_MAX_SPEAKERS,
            },
            // Curated mode only, in the order the organizer arranged them.
            speakerIds: {
              type: "array",
              items: { type: "integer", minimum: 1 },
              uniqueItems: true,
              maxItems: HOME_SCREEN_MAX_SPEAKERS,
            },
          },
          additionalProperties: false,
        },
      },
      propertyNames: { enum: HOME_SCREEN_SECTION_KEYS },
      additionalProperties: { type: "object" },
    },
  },
  additionalProperties: false,
};

export const eventConfigSchema = {
  type: "object",
  properties: {
    logoImage: { type: ["string", "null"] },
    headerImage: { type: ["string", "null"] },
    accentColor: {
      type: ["string", "null"],
      pattern: "^#([0-9A-Fa-f]{3}){1,2}$",
    },
    defaultProfileCover: { type: ["string", "null"] },
    defaultSessionCover: { type: ["string", "null"] },
    defaultSponsorCover: { type: ["string", "null"] },
    defaultSpeakerCover: { type: ["string", "null"] },
    homeScreen: homeScreenSchema,
    setupSkipped: {
      type: "array",
      items: { type: "string" },
      uniqueItems: true,
    },
  },
};

export const getEventsSchema = {
  querystring: {
    type: "object",
    properties: {
      workspace: { type: "number" },
      page: { type: "number" },
      limit: { type: "number" },
      sort: { type: "string" },
      ...languageSchema.properties,
    },
  },
};

export const getEventByIdSchema = {
  querystring: languageSchema,
  params: {
    type: "object",
    properties: {
      id: { type: "number" },
    },
    required: ["id"],
  },
};

// Public page entry point: the event is looked up by slug only.
export const getPublicEventBySlugSchema = {
  querystring: languageSchema,
  params: {
    type: "object",
    properties: {
      slug: { type: "string", minLength: 1 },
    },
    required: ["slug"],
  },
};

export const checkEventSlugAvailabilitySchema = {
  querystring: {
    type: "object",
    properties: {
      slug: slugSchema,
      excludeEventId: { type: "number" },
    },
    required: ["slug"],
  },
};

export const createEventSchema = {
  type: "object",
  properties: {
    wsId: { type: "integer" },
    nameI18n: makeI18nField(),
    mainLocation: { type: "string" },
    contactEmail: { type: "string" },
    config: { type: "string" },
    isPublic: { type: "boolean" },
    language: { type: "string", pattern: "^[a-z]{2}$" },
    secondaryLanguages: {
      anyOf: [
        { type: "array", items: { type: "string", pattern: "^[a-z]{2}$" } },
        { type: "null" },
      ],
    },
    autoCloseDays: { type: ["integer", "null"], minimum: 1 },
    enabledFeatures: {
      type: "array",
      items: {
        type: ["string"],
      },
    },
    attendeeLimit: { type: ["integer", "null"] },
    slug: slugSchema,
  },
  required: ["wsId", "nameI18n"],
  additionalProperties: false,
};

export const eventSchema = {
  type: "object",
  properties: {
    wsId: { type: "integer" },
    name: { type: "string", minLength: 1, maxLength: 255 },
    nameI18n: makeI18nField(),
    status: { type: "string", enum: ["draft", "active", "closed"] },
    mainLocation: { type: ["string", "null"] },
    contactEmail: { type: ["string", "null"] },
    config: eventConfigSchema,
    isPublic: { type: "boolean" },
    language: { type: "string", pattern: "^[a-z]{2}$" },
    secondaryLanguages: {
      anyOf: [
        { type: "array", items: { type: "string", pattern: "^[a-z]{2}$" } },
        { type: "null" },
      ],
    },
    autoCloseDays: { type: ["integer", "null"], minimum: 1 },
    attendeeLimit: { type: ["integer", "null"] },
    slug: storedSlugSchema,
  },
};

export const updateEventSchema = _.merge({}, eventSchema, {
  properties: {
    dates: {
      type: "array",
      items: {
        type: "string",
        format: "date",
      },
    },
  },
});

export const createEventLocationSchema = {
  type: "object",
  properties: {
    eventId: { type: "integer" },
    nameI18n: makeI18nField(),
    descriptionI18n: makeI18nField(),
    picture: { type: "string" },
  },
  required: ["eventId", "nameI18n"],
  additionalProperties: false,
};

export const deleteEventSchema = {
  body: {
    type: "object",
    properties: {
      id: { type: "number" },
    },
    required: ["id"],
  },
};

export const getEventLocationsSchema = {
  querystring: languageSchema,
  params: {
    type: "object",
    properties: {
      id: { type: "number" },
    },
  },
  required: ["id"],
};

export const getPublicEventLocationsSchema = {
  querystring: languageSchema,
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
};

export const getPublicEventDatesSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
};

export const getEventLocationByLocationIdSchema = {
  querystring: languageSchema,
  params: {
    type: "object",
    properties: {
      id: { type: "number" },
      locationId: { type: "number" },
    },
    required: ["id", "locationId"],
  },
};

const patterns = {
  trimmedString: "^(?! ).+(?<! )$",
};

export const web_patchCustomEventSchema = {
  type: "object",
  properties: {
    nameI18n: makeI18nField({ required: true, minLength: 1 }),
    mainLocation: { type: ["string", "null"] },
    status: { type: "string", enum: ["draft", "active", "closed"] },
    slug: storedSlugSchema,
    language: { type: "string", pattern: "^[a-z]{2}$" },
    secondaryLanguages: {
      type: "array",
      items: { type: "string", pattern: "^[a-z]{2}$" },
    },
    contactEmail: {
      anyOf: [
        { type: "string", format: "email" },
        { type: ["string", "null"], maxLength: 0 },
      ],
    },
    config: _.merge({}, eventConfigSchema, {
      properties: {
        accentColor: {
          type: "string",
          errorMessage: "Invalid hex color",
        },
      },
    }),
  },
};

export const web_locationFormSchema = {
  type: "object",
  properties: {
    nameI18n: makeI18nField({ required: true, minLength: 1 }),
    descriptionI18n: makeI18nField(),
    picture: { type: ["string", "null"] },
    eventId: { type: "number" },
  },
  required: ["nameI18n", "eventId"],
};

export const web_createEventSchema = {
  type: "object",
  properties: {
    wsId: {
      type: ["string"],
    },
    nameI18n: {
      type: "object",
      properties: {
        en: {
          type: "string",
          minLength: 1,
          pattern: patterns.trimmedString,
        },
      },
    },
    autoCloseDays: { type: ["integer", "null"], minimum: 1 },
    enabledFeatures: {
      type: "array",
      items: {
        type: "string",
      },
      default: [],
    },
    attendeeLimit: { type: ["integer", "null"] },
  },
};

export const web_editEventSchema = {
  type: "object",
  properties: {
    name: { type: "string", minLength: 1, pattern: patterns.trimmedString },
    status: { type: "string", enum: ["draft", "active", "closed"] },
    autoCloseDays: { type: ["integer", "null"], minimum: 1 },
    enabledFeatures: {
      type: "array",
      items: {
        type: ["string"],
      },
    },
    attendeeLimit: { type: ["integer", "null"] },
    slug: { type: "string" },
  },
  required: ["name"],
};

export const getEventDocumentsSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
  querystring: {
    type: "object",
    properties: {
      page: { type: "number" },
      limit: { type: "number" },
      distributionId: { type: "number" },
    },
  },
};
